import React from 'react';
import { setSelectedTask } from '../../../actions/tools/OrdersSearchActions';
import { shopifyUpdateOrderState } from '../../../actions/tools/ShopifyActions';
import { fetchShippedInfo, refundShippedItems } from '../../../actions/tools/CommonOrderActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { addNoteToSelectedTicket } from '../../../actions';
import { extractSelectedInboxTab, extractSelectedTicketFromState, isSpecialist } from '../../../extractFromState';

import { getSelectedOrder, getSelectedShop, selectSelectedToolDomains } from '../../../selectors/tools';
import PropTypes from 'prop-types';
import Loading from '../../LoadingComponent/LoadingComponent';
import moment from 'moment';
import { Button, FullColoredButton } from '../../common/buttonsComponents';
import * as Styled from '../common/CommonStyledComponents';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../popups/ConfirmPopup/helper';
import cloneDeep from 'lodash/cloneDeep';
import ShopifyPolicy from './ShopifyPolicy/ShopifyPolicy';
import OrderItemsSelectItem from '../common/OrderItemsSelectItem';
import OrderSummaryBoxFromServer from '../common/OrderSummaryBoxFromServer';
import get from 'lodash/get';
import BigNumber from 'bignumber.js';
import { handleError } from 'services/ErrorService/ErrorService';

const DESCRIPTION_CANCEL =
  'You cannot cancel this order because the order has already been cancelled. Please select another action instead..';
const DESCRIPTION_NO_ITEM = `All refundable items in this order were already refunded.`;
const DESCRIPTION = `The customer has paid for this order and it has been shipped, so you can refund this order.`;

class ShopifyIssueRefund extends React.Component {
  static propTypes = {
    selectedOrder: PropTypes.object.isRequired,
    selectedTicket: PropTypes.object,
    setSelectedTask: PropTypes.func,
    selectedToolDomains: PropTypes.object,
    fetchShippedInfo: PropTypes.func,
    refundShippedItems: PropTypes.func,
    isSpecialist: PropTypes.bool,
  };

  static defaultProps = {
    setSelectedTask: () => {},
    fetchShippedInfo: () => Promise.resolve(),
    refundShippedItems: () => Promise.resolve(),
  };

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { status, refundableShippedItems, currencyCode, customData } = await this.props.fetchShippedInfo({
      orderId: this.orderId,
    });

    if (this._isMounted === false) {
      return;
    }

    const selectedItems = {};
    for (let refundableItem of refundableShippedItems) {
      selectedItems[refundableItem['id']] = {
        qty: refundableItem['maxRefundableShipped'],
        returnPolicy: refundableItem['returnPolicy'],
        active: true,
      };
    }

    this.setState({
      name: (customData && customData.name) || this.orderId,
      selectedItems,
      status,
      refundableItems: refundableShippedItems,
      currencyCode,
      isRefundOrderItemsRequesting: false,
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  state = {
    status: '',
    currencyCode: 'USD',
    refundableItems: [],
    shippedItems: [],
    selectedItems: {},
    reasonComment: '',
    isRefundRequesting: false,
    isRefundOrderItemsRequesting: true,
    isUpdateOrderRequesting: false,
    isPolicyAccepted: false,
    summaryBlock: {},
  };

  get orderId() {
    return String(this.props.selectedOrder.id);
  }

  get shopifyConfig() {
    const configs = this.props.selectedToolDomains['shopifyConfigs'] || {};

    return configs[this.props.selectedShop] || {};
  }

  get hasRefundableItems() {
    return this.state.status !== 'cancelled' && !!this.state.refundableItems.length;
  }

  get placeholderText() {
    if (this.state.status === 'cancelled') {
      return DESCRIPTION_CANCEL;
    }

    if (!this.hasRefundableItems) {
      return DESCRIPTION_NO_ITEM;
    }

    return DESCRIPTION;
  }

  get refundConfig() {
    return this.shopifyConfig.refundConfig || {};
  }

  get refundPolicy() {
    return this.refundConfig.refundPolicy || {};
  }

  get refundLimitPrice() {
    const maxPriceAllowed = this.refundPolicy['maxPriceAllowed'];

    return typeof maxPriceAllowed === 'undefined' ? 0 : parseInt(maxPriceAllowed);
  }

  handleItemQtyChange = (itemId, newQty) => {
    const selectedItems = cloneDeep(this.state.selectedItems);
    selectedItems[itemId].qty = newQty;
    if (BigNumber(newQty).isEqualTo(BigNumber(0))) {
      selectedItems[itemId].active = !selectedItems[itemId].active;
    }
    this.setState({ selectedItems });
  };

  handleSelectItem = (itemId) => {
    const selectedItems = cloneDeep(this.state.selectedItems);
    selectedItems[itemId].active = !selectedItems[itemId].active;
    this.setState({ selectedItems });
  };

  get isAllItemsSelected() {
    return this.state.refundableItems.every((item) => {
      const selectedItem = this.state.selectedItems[item['id']];

      return item['maxRefundableShipped'] === selectedItem.qty && selectedItem.active;
    });
  }

  handleSelectAll = () => {
    const selectedItems = cloneDeep(this.state.selectedItems);
    const isAllItemsSelected = this.isAllItemsSelected;
    this.state.refundableItems.forEach((item) => {
      selectedItems[item['id']].active = !isAllItemsSelected;

      if (!this.isAllItemsSelected) {
        selectedItems[item['id']].qty = item['maxRefundableShipped'];
      }
    });

    this.setState({ selectedItems });
  };

  get refundLimitDays() {
    const refundPolicy = this.refundConfig.refundPolicy || {};

    return typeof refundPolicy.days === 'undefined' ? null : refundPolicy.days;
  }

  get isSpecialist() {
    return this.props.isSpecialist;
  }

  get createdDate() {
    return (this.props.selectedOrder || {}).created_at || '';
  }

  get totalRefund() {
    return get(this, 'state.summaryBlock.totals.total', 0);
  }

  isRefundLimitExceeded() {
    return this.refundLimitPrice !== 0 ? this.refundLimitPrice <= this.totalRefund : false;
  }

  get selectedQty() {
    return this.state.refundableItems.reduce((accumulator, item) => {
      const selectedItem = this.state.selectedItems[item['id']];
      const qty = selectedItem.active ? selectedItem.qty : 0;

      return accumulator + qty;
    }, 0);
  }

  displayPopupErrorMessage(message = '') {
    oneBtnPopupCreator({
      content: {
        main: message,
        iconPath: '/images/error-esclamation.svg',
      },
      btnConfig: 'CLOSE',
    });
  }

  handleRefundBtnClick = () => {
    if (this.selectedQty === 0) {
      return this.displayPopupErrorMessage('Please add at least one item');
    }
    if (!this.state.reasonComment.trim()) {
      return this.displayPopupErrorMessage('Please add reason for cancel');
    }

    if (this.isRefundLimitExceeded()) {
      return this.displayPopupErrorMessage(`Partner limits for refund $${this.refundLimitPrice}`);
    }

    twoBtnPopupCreator({
      content: {
        main: 'Are you sure you want to refund the order?',
        iconPath: '/images/green-alert-bell.svg',
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: 'REFUND ORDER',
      },
      onConfirm: this.confirmationHandler,
    });
  };

  handlerTextInputChange = ({ target: { name, value } }) => {
    this.setState({ [name]: value });
  };

  onChangeTotals = (totals) => {
    this.setState({ summaryBlock: { ...this.state.summaryBlock, ...totals } });
  };

  get refundItems() {
    return this.state.refundableItems
      .map((item) => {
        const selectedItem = this.state.selectedItems[item['id']];
        const qty = selectedItem.active ? selectedItem.qty : 0;

        return {
          id: item['id'],
          quantity: qty,
        };
      })
      .filter((refundItem) => {
        return refundItem.quantity > 0;
      });
  }

  confirmationHandler = async () => {
    this.setState({ isRefundRequesting: true });

    const payload = {
      orderId: this.orderId,
      refundItems: this.refundItems,
      comment: this.state.reasonComment,
      conversationId: this.props.selectedTicket.ticketId,
      addNotes: true,
      timeOut: 70000,
    };

    let refund;
    try {
      refund = await this.props.refundShippedItems(payload);
    } catch (error) {
      handleError({ error });

      return this.handleFailedRefund();
    }

    await this.updateSelectedOrder();
    await this.handleSuccessRefund(refund);
  };

  handleBackBtnClick = () => {
    this.props.setSelectedTask(null);
  };

  async updateSelectedOrder() {
    this.setState({ isUpdateOrderRequesting: true });
    try {
      await this.props.shopifyUpdateOrderState(this.orderId, this.props.selectedShop);
    } catch (error) {
      handleError({ error });
    }
    this.setState({ isUpdateOrderRequesting: false });
  }

  handleSuccessRefund = async (refund) => {
    this.setState({ isRefundRequesting: false });

    this.props.setSelectedTask(null);

    oneBtnPopupCreator({
      content: {
        main: `Refund order successful: Refunded ${refund.amount.toFixed(2)}${this.state.currencyCode}`,
        iconPath: '/images/checkmark-green.svg',
      },
      btnConfig: 'CLOSE',
    });
  };

  handleFailedRefund = () => {
    this.setState({ isRefundRequesting: false });

    oneBtnPopupCreator({
      content: {
        main: 'Refund order failed',
        iconPath: '/images/error-esclamation.svg',
      },
      btnConfig: 'CLOSE',
    });
  };

  isRefundDateLimitExceeded() {
    const createdDateMoment = moment(this.createdDate).format('MM/DD/YYYY');
    const actualDate = moment(new Date()).format('MM/DD/YYYY');

    return !(moment(actualDate).diff(moment(createdDateMoment), 'days') <= this.refundLimitDays);
  }

  renderComponent() {
    const refundableItemsJSX = this.state.refundableItems.map((item) => {
      const itemId = item['id'];
      const selectedItem = this.state.selectedItems[itemId];

      return (
        <OrderItemsSelectItem
          id={itemId}
          active={selectedItem.active}
          name={`(${item['maxRefundableShipped']}) ${item['name']}`}
          max={item['maxRefundableShipped']}
          qty={selectedItem.qty}
          handleSelectItem={this.handleSelectItem}
          handleItemQtyChange={this.handleItemQtyChange}
        />
      );
    });

    return (
      <Styled.OrderTaskWrapper>
        <Styled.OrderTaskTitle>Return/Refund Order</Styled.OrderTaskTitle>
        <Styled.OrderTaskDescription>{this.placeholderText}</Styled.OrderTaskDescription>

        {this.hasRefundableItems && (
          <Styled.CancelStep>
            <Styled.StepTitle>
              <Styled.Subtext>Step 1 - </Styled.Subtext>
              Following items can be refund. Please select product (s) and change quantity if different.
            </Styled.StepTitle>

            <Styled.ItemRow>
              <Styled.Checkbox
                type="checkbox"
                checked={this.isAllItemsSelected}
                onChange={() => this.handleSelectAll()}
              />
              <Styled.RowContent>
                <Styled.Subtext>Select all</Styled.Subtext>
              </Styled.RowContent>
            </Styled.ItemRow>

            <Styled.Divider />

            {refundableItemsJSX}

            <Styled.Divider />
          </Styled.CancelStep>
        )}

        {this.hasRefundableItems && (
          <Styled.CancelStep>
            <Styled.StepTitle>
              <Styled.Subtext>Step 2 - </Styled.Subtext>Select reason for refund <Styled.Required />
            </Styled.StepTitle>

            <Styled.RowContent>
              <Styled.RefundCommentTextarea
                name="reasonComment"
                value={this.state.reasonComment}
                onChange={this.handlerTextInputChange}
              />

              <Styled.FlexPlaceholder />

              <OrderSummaryBoxFromServer
                onChange={(totals) => this.onChangeTotals(totals)}
                currencyCode={this.state.currencyCode}
                refundItems={this.refundItems}
              />
            </Styled.RowContent>
          </Styled.CancelStep>
        )}

        <Styled.OrderTaskControls>
          <Button onClick={this.handleBackBtnClick}>BACK</Button>
          {this.hasRefundableItems && (
            <FullColoredButton
              active={this.state.summaryBlock && !this.state.summaryBlock.request}
              onClick={this.handleRefundBtnClick}
              disabled={this.state.summaryBlock && this.state.summaryBlock.request}
            >
              REFUND ORDER
            </FullColoredButton>
          )}
        </Styled.OrderTaskControls>
      </Styled.OrderTaskWrapper>
    );
  }

  render() {
    if (this.state.isRefundRequesting || this.state.isUpdateOrderRequesting) {
      return <Loading label="Refund order..." />;
    }
    if (this.state.isRefundOrderItemsRequesting) {
      return <Loading label="Loading refund information..." />;
    }

    return (
      <ShopifyPolicy
        createdDate={this.createdDate}
        showCancel={false}
        showRefund={true}
        config={this.shopifyConfig}
        isSpecialist={this.isSpecialist}
      >
        {this.renderComponent()}
      </ShopifyPolicy>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrder(state),
  selectedTicket: extractSelectedTicketFromState(state),
  selectedInboxTab: extractSelectedInboxTab(state),
  selectedShop: getSelectedShop(state),
  selectedToolDomains: selectSelectedToolDomains(state),
  isSpecialist: isSpecialist(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSelectedTask,
      fetchShippedInfo,
      refundShippedItems,
      addNoteToSelectedTicket,
      shopifyUpdateOrderState,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyIssueRefund);
